/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
// Third-party
import { css } from '@emotion/react'
import { App } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { useHistory, useParams } from 'react-router-dom'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Icon from 'src/components/Icon'
import SchedulerErrorBoundary from './components/SchedulerErrorBoundary'
import CalendarGrid from './components/CalendarGrid'
import WeekGrid from './components/WeekGrid'
import DayGrid from './components/DayGrid'
import AgendaView from './components/AgendaView'
import type { CalendarGame } from './components/CalendarGameCard'
import CalendarGameEditPopover from './components/CalendarGameEditPopover'
import ScheduleFilters, {
  DEFAULT_FILTER_VALUES,
  type ScheduleFilterValues,
} from './components/ScheduleFilters'
import { useSchedulerCompetition } from './hooks/useSchedulerCompetition'
import { useSchedulerRequest } from './hooks/useSchedulerRequest'
import { useSchedulerLocations } from './hooks/useSchedulerLocations'
import { useSchedulerDivisions } from './hooks/useSchedulerDivisions'
import { useSchedulerTimeSlots } from './hooks/useSchedulerTimeSlots'
import { useSchedulerConstraints } from './hooks/useSchedulerConstraints'
import { useGameEdit } from './hooks/useGameEdit'
import type { GameEditData } from './hooks/useGameEdit'
import { useGameMutation } from './hooks/useGameMutation'
import { useUndoStack } from './hooks/useUndoStack'
import PreflightValidationModal from './components/PreflightValidationModal'
import { transformGamesToDisplay } from './utils/transformGames'
import { generateGamesCsv, downloadCsv } from './utils/csvExport'
import type { GeneratedGame } from './components/GeneratedGamesSection'
import { useCheckpoint } from './hooks/useCheckpoint'

interface RouteParams {
  competitionId: string
  requestId: string
}

// ─── Division Colors ─────────────────────────────────────────────
const DIVISION_COLORS = [
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#10B981', // Green
  '#EC4899', // Pink
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#F59E0B', // Amber
]

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const MONTH_ABBREVS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// ─── Styles (module-level) ───────────────────────────────────────

const pageContainerStyles = css`
  min-height: 100vh;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const headerStyles = css`
  background-color: ${colors.SECONDARY_400};
  padding: 40px 120px;
`

const headerInnerStyles = css`
  padding: 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const leftSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const backLinkStyles = css`
  color: ${colors.PRIMARY_300};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 0;
  transition: color 0.2s ease;
  line-height: 16.8px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

const titleStyles = css`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 48px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin: 0;
  line-height: 48px;
`

const exportButtonStyles = css`
  padding: 8px 16px;
  background-color: transparent;
  border: 2px solid ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 23.2px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.PRIMARY_300};
    color: ${colors.SECONDARY_300};
  }
`

const contentContainerStyles = css`
  flex: 1;
  padding: 40px 120px;
  background-color: ${colors.SECONDARY_500};
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const viewToggleContainerStyles = css`
  display: flex;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;
`

const viewToggleBtnStyles = (active: boolean) => css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  padding: 4px 14px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${active ? colors.WHITE : colors.NEUTRAL_50};
  background: ${active ? colors.SECONDARY_200 : 'transparent'};

  &:hover {
    background: ${active ? colors.SECONDARY_200 : colors.SECONDARY_300};
  }
`

const calendarNavStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: ${colors.SECONDARY_300};
`

const calendarNavBtnStyles = css`
  background: transparent;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
    color: ${colors.WHITE};
  }
`

const calendarMonthTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: ${colors.WHITE};
  min-width: 180px;
  text-align: center;
`

const gridContainerStyles = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const toolbarStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const actionBtnStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_300}20;
  }
`

const actionBtnTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.PRIMARY_300};
  margin: 0;
`

const loadingStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 0;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: ${colors.NEUTRAL_50};
`

/**
 * Calendar Page — dedicated page for Month/Week/List schedule views.
 * Fetches all data independently via hooks, renders calendar grids
 * with popover editing and drag-and-drop.
 */
const CalendarPage: React.FC = () => {
  const history = useHistory()
  const { competitionId, requestId } = useParams<RouteParams>()
  const { message, modal } = App.useApp()

  // ─── Data fetching ─────────────────────────────────────────────
  const { competition } = useSchedulerCompetition(competitionId)
  const { request: schedulerRequest, fetchRequest } =
    useSchedulerRequest(requestId)
  const { venues } = useSchedulerLocations(competitionId)
  const { divisions } = useSchedulerDivisions(
    competitionId,
    schedulerRequest?.approved_division_uids
  )
  const { timeSlots } = useSchedulerTimeSlots(competitionId)
  const { constraints } = useSchedulerConstraints(requestId)
  const {
    bulkApproveGames,
    bulkRevertGames,
    approving: gameApproving,
    error: gameEditError,
    clearError: clearGameEditError,
  } = useGameEdit()

  const refreshAfterMutation = useCallback(() => {
    if (requestId) {
      fetchRequest(requestId)
    }
  }, [requestId, fetchRequest])

  const {
    preview: mutationPreview,
    modalVisible: mutationModalVisible,
    loading: mutationLoading,
    confirming: mutationConfirming,
    error: mutationError,
    requestPreview,
    confirmMutation,
    selectAlternativeSlot,
    cancelMutation,
  } = useGameMutation(refreshAfterMutation)

  const {
    canUndo,
    canRedo,
    lastEntry,
    nextRedoEntry,
    pushEntry,
    undo,
    redo,
    clear: clearUndoStack,
  } = useUndoStack(refreshAfterMutation)

  const { isSaving, isRestoring, saveCheckpoint, restoreCheckpoint } =
    useCheckpoint()

  // ─── Checkpoint handlers ──────────────────────────────────────
  const handleSaveCheckpoint = useCallback(async () => {
    if (!requestId) {
      return
    }
    const doSave = async () => {
      const success = await saveCheckpoint(requestId)
      if (success) {
        await fetchRequest(requestId)
        message.success('Saved')
      } else {
        message.error('Failed to save')
      }
    }

    if (schedulerRequest?.has_checkpoint) {
      modal.confirm({
        title: 'Overwrite Save',
        content: 'This will overwrite the existing save point. Are you sure?',
        okText: 'Overwrite',
        onOk: doSave,
        centered: true,
      })
    } else {
      await doSave()
    }
  }, [
    requestId,
    schedulerRequest?.has_checkpoint,
    saveCheckpoint,
    fetchRequest,
    message,
    modal,
  ])

  const handleRestoreCheckpoint = useCallback(async () => {
    if (!requestId) {
      return
    }
    modal.confirm({
      title: 'Undo Changes',
      content:
        'All changes since the last save will be lost. This cannot be undone.',
      okText: 'Undo',
      okButtonProps: { danger: true },
      onOk: async () => {
        const { success } = await restoreCheckpoint(requestId)
        if (success) {
          clearUndoStack()
          await fetchRequest(requestId)
          message.success('Changes undone')
        } else {
          message.error('Failed to undo changes')
        }
      },
      centered: true,
    })
  }, [
    requestId,
    restoreCheckpoint,
    fetchRequest,
    message,
    modal,
    clearUndoStack,
  ])

  // ─── Move mode state ───────────────────────────────────────────
  const [moveMode, setMoveMode] = useState<{
    gameId: string
    game: GeneratedGame
  } | null>(null)

  // Escape key exits move mode
  useEffect(() => {
    if (!moveMode) {
      return
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMoveMode(null)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [moveMode])

  // ─── Transform games ──────────────────────────────────────────
  const displayGames: GeneratedGame[] = useMemo(() => {
    if (!schedulerRequest?.games) {
      return []
    }
    return transformGamesToDisplay(schedulerRequest.games, divisions)
  }, [schedulerRequest?.games, divisions])

  // ─── Filter state ─────────────────────────────────────────────
  const [gameFilters, setGameFilters] = useState<ScheduleFilterValues>(
    DEFAULT_FILTER_VALUES
  )

  const filterOptions = useMemo(() => {
    const divisionSet = new Map<string, string>()
    const teamSet = new Map<string, string>()
    const locationSet = new Map<string, string>()
    const surfaceSet = new Map<string, string>()

    for (const game of displayGames) {
      if (game.divisionId) {
        divisionSet.set(game.divisionId, game.division)
      }
      if (game.homeTeam && game.homeTeam !== 'TBD') {
        teamSet.set(game.homeTeam, game.homeTeam)
      }
      if (game.awayTeam && game.awayTeam !== 'TBD') {
        teamSet.set(game.awayTeam, game.awayTeam)
      }
      if (game.location && game.location !== 'Unknown Venue') {
        locationSet.set(game.location, game.location)
      }
      if (game.surface && game.surface !== 'Unknown Surface') {
        surfaceSet.set(game.surface, game.surface)
      }
    }

    const toOptions = (map: Map<string, string>) =>
      Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return {
      divisions: toOptions(divisionSet),
      teams: toOptions(teamSet),
      locations: toOptions(locationSet),
      surfaces: toOptions(surfaceSet),
      statuses: [
        { value: 'approved', label: 'Approved' },
        { value: 'pending', label: 'Pending' },
        { value: 'rejected', label: 'Rejected' },
      ],
    }
  }, [displayGames])

  const filteredGames: GeneratedGame[] = useMemo(() => {
    return displayGames.filter((game) => {
      if (
        gameFilters.division.length > 0 &&
        !gameFilters.division.includes(game.divisionId)
      ) {
        return false
      }
      if (
        gameFilters.team.length > 0 &&
        !gameFilters.team.includes(game.homeTeam) &&
        !gameFilters.team.includes(game.awayTeam)
      ) {
        return false
      }
      if (
        gameFilters.location.length > 0 &&
        !gameFilters.location.includes(game.location)
      ) {
        return false
      }
      if (
        gameFilters.surface.length > 0 &&
        !gameFilters.surface.includes(game.surface)
      ) {
        return false
      }
      if (gameFilters.status.length > 0) {
        const gameStatus = game.approvalStatus ?? 'pending'
        if (!gameFilters.status.includes(gameStatus)) {
          return false
        }
      }
      return true
    })
  }, [displayGames, gameFilters])

  // Wrap confirmMutation to push undo entry after success
  const handleConfirmMutation = useCallback(
    async (overrideReason?: string) => {
      if (!mutationPreview) {
        return false
      }
      const preview = mutationPreview

      // Capture before state from the game data
      const game = filteredGames.find((g) => g.id === preview.gameId)
      const beforeData: GameEditData = game
        ? {
            starts_at: game.startsAt,
            ends_at: game.endsAt,
            venue_id: game.locationId as unknown as number,
            facility_id: game.surfaceId as unknown as number,
          }
        : {}

      const success = await confirmMutation(overrideReason)
      if (success) {
        const description =
          preview.type === 'swap'
            ? `Swapped Game #${preview.gameId} ↔ #${preview.secondGameId}`
            : `Moved Game #${preview.gameId}`

        pushEntry({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          timestamp: Date.now(),
          type: preview.type,
          description,
          gameId: preview.gameId,
          secondGameId: preview.secondGameId,
          before: beforeData,
          after: preview.resolvedData,
        })
      }
      return success
    },
    [mutationPreview, confirmMutation, pushEntry, filteredGames]
  )

  // ─── Calendar state ───────────────────────────────────────────
  const [viewMode, setViewMode] = useState<
    'calendar' | 'week' | 'day' | 'table'
  >('calendar')

  const earliestGameDate = useMemo(() => {
    if (filteredGames.length === 0) {
      return new Date()
    }
    const dates = filteredGames
      .map((g) => (g.startsAt ? new Date(g.startsAt) : new Date(g.date)))
      .filter((d) => !isNaN(d.getTime()))
    if (dates.length === 0) {
      return new Date()
    }
    dates.sort((a, b) => a.getTime() - b.getTime())
    return dates[0]
  }, [filteredGames])

  const [currentDate, setCurrentDate] = useState<Date | null>(null)
  const calendarDate = currentDate ?? earliestGameDate
  const calendarYear = calendarDate.getFullYear()
  const calendarMonth = calendarDate.getMonth()

  const handlePrevMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth() - 1, 1)
    })
  }, [earliestGameDate])

  const handleNextMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth() + 1, 1)
    })
  }, [earliestGameDate])

  // ─── Week navigation ──────────────────────────────────────────
  const weekStart = useMemo(() => {
    const d = new Date(calendarDate)
    d.setDate(d.getDate() - d.getDay()) // rewind to Sunday
    d.setHours(0, 0, 0, 0)
    return d
  }, [calendarDate])

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + 6)
    return d
  }, [weekStart])

  const weekRangeLabel = useMemo(() => {
    const s = weekStart
    const e = weekEnd
    const sMonth = MONTH_ABBREVS[s.getMonth()]
    const eMonth = MONTH_ABBREVS[e.getMonth()]
    const sDay = s.getDate()
    const eDay = e.getDate()
    const eYear = e.getFullYear()
    if (s.getMonth() === e.getMonth()) {
      return `${sMonth} ${sDay} – ${eDay}, ${eYear}`
    }
    return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${eYear}`
  }, [weekStart, weekEnd])

  const handlePrevWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7)
    })
  }, [earliestGameDate])

  const handleNextWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7)
    })
  }, [earliestGameDate])

  // ─── Day navigation ────────────────────────────────────────────
  const DAY_NAMES = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ]

  const dayLabel = useMemo(() => {
    const d = calendarDate
    const monthAbbrev = MONTH_ABBREVS[d.getMonth()].toUpperCase()
    return `${
      DAY_NAMES[d.getDay()]
    }, ${d.getDate()} ${monthAbbrev} ${d.getFullYear()}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDate])

  const handlePrevDay = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1)
    })
  }, [earliestGameDate])

  const handleNextDay = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
    })
  }, [earliestGameDate])

  // ─── Division color map ───────────────────────────────────────
  const divisionColorMap = useMemo(() => {
    const map = new Map<string, string>()
    const uniqueIds: string[] = []
    for (const g of filteredGames) {
      if (g.divisionId && !map.has(g.divisionId)) {
        map.set(
          g.divisionId,
          DIVISION_COLORS[uniqueIds.length % DIVISION_COLORS.length]
        )
        uniqueIds.push(g.divisionId)
      }
    }
    return map
  }, [filteredGames])

  // ─── Calendar games transform ─────────────────────────────────
  const calendarGames: CalendarGame[] = useMemo(() => {
    return filteredGames.map((game) => ({
      id: game.id,
      date: game.startsAt ? new Date(game.startsAt) : new Date(game.date),
      time: game.time,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      venue: game.location,
      divisionColor:
        divisionColorMap.get(game.divisionId) ?? DIVISION_COLORS[0],
      homeTeamId: game.homeTeamId ?? '',
      awayTeamId: game.awayTeamId ?? '',
      locationId: game.locationId ?? '',
      surfaceId: game.surfaceId ?? '',
      divisionId: game.divisionId,
      startsAt: game.startsAt ?? '',
      endsAt: game.endsAt ?? '',
      approvalStatus: game.approvalStatus ?? 'pending',
      gameNumber: game.gameNumber,
    }))
  }, [filteredGames, divisionColorMap])

  // ─── Blackout dates ───────────────────────────────────────────
  const calendarBlackoutDates = useMemo(() => {
    const dates: Date[] = []
    for (const c of constraints) {
      if (c.type.code !== 'venue_blackout' || !c.is_enabled) {
        continue
      }
      const params = c.parameters as { dates?: string[] }
      for (const d of params.dates || []) {
        dates.push(new Date(d + 'T00:00:00'))
      }
    }
    return dates
  }, [constraints])

  const calendarBlackoutDateStrings = useMemo(() => {
    const set = new Set<string>()
    for (const d of calendarBlackoutDates) {
      set.add(d.toISOString().slice(0, 10))
    }
    return set
  }, [calendarBlackoutDates])

  // ─── Competition date boundaries ──────────────────────────────
  const minDate = useMemo(
    () =>
      competition?.starts_at
        ? dayjs.utc(competition.starts_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.starts_at]
  )

  const maxDate = useMemo(
    () =>
      competition?.ends_at
        ? dayjs.utc(competition.ends_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.ends_at]
  )

  // ─── Game edit handlers ───────────────────────────────────────
  const handleBulkApprove = useCallback(
    async (gameIds: string[]) => {
      if (!requestId) {
        return
      }
      const success = await bulkApproveGames(requestId, gameIds)
      if (success) {
        fetchRequest(requestId)
      }
    },
    [requestId, bulkApproveGames, fetchRequest]
  )

  const handleBulkRevert = useCallback(
    async (gameIds: string[]) => {
      if (!requestId) {
        return
      }
      const success = await bulkRevertGames(requestId, gameIds)
      if (success) {
        fetchRequest(requestId)
      }
    },
    [requestId, bulkRevertGames, fetchRequest]
  )

  // ─── Popover state ────────────────────────────────────────────
  const [popoverGameId, setPopoverGameId] = useState<string | null>(null)
  const popoverSnapshotRef = useRef<{
    locationId: string
    surfaceId: string
    homeTeamId: string
    awayTeamId: string
    startsAt: string
    endsAt: string
  } | null>(null)

  const popoverGame = useMemo(
    () =>
      popoverGameId
        ? calendarGames.find((g) => g.id === popoverGameId) ?? null
        : null,
    [popoverGameId, calendarGames]
  )

  const handleCalendarGameClick = useCallback((game: CalendarGame) => {
    if (game.approvalStatus === 'approved') {
      return
    }
    popoverSnapshotRef.current = {
      locationId: game.locationId,
      surfaceId: game.surfaceId,
      homeTeamId: game.homeTeamId,
      awayTeamId: game.awayTeamId,
      startsAt: game.startsAt,
      endsAt: game.endsAt,
    }
    setPopoverGameId(game.id)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverGameId(null)
    popoverSnapshotRef.current = null
  }, [])

  const handlePopoverSave = useCallback(
    async (gameId: string, data: Record<string, unknown>): Promise<boolean> => {
      if (!requestId) {
        return false
      }

      setPopoverGameId(null)
      // Route through preflight validation modal
      requestPreview(requestId, gameId, data as GameEditData)
      return true
    },
    [requestId, requestPreview]
  )

  // Click-to-move: when move mode is active, handle day clicks
  const handleMoveModeClick = useCallback(
    (targetDate: Date) => {
      if (!moveMode || !requestId) {
        return
      }

      const tY = targetDate.getFullYear()
      const tM = String(targetDate.getMonth() + 1).padStart(2, '0')
      const tD = String(targetDate.getDate()).padStart(2, '0')
      const targetDateStr = `${tY}-${tM}-${tD}`

      requestPreview(requestId, moveMode.gameId, {
        target_date: targetDateStr,
      })
      setMoveMode(null)
    },
    [moveMode, requestId, requestPreview]
  )

  const handleEnterMoveMode = useCallback(
    (gameId: string) => {
      const game = filteredGames.find((g) => g.id === gameId)
      if (!game) {
        return
      }
      setPopoverGameId(null)
      setMoveMode({ gameId, game })
    },
    [filteredGames]
  )

  const handleExitMoveMode = useCallback(() => {
    setMoveMode(null)
  }, [])

  const popoverContent = useMemo(() => {
    if (!popoverGame) {
      return null
    }
    return (
      <CalendarGameEditPopover
        game={popoverGame}
        venues={venues}
        divisions={divisions}
        timeSlots={timeSlots}
        onClose={handlePopoverClose}
        onSave={handlePopoverSave}
        onMoveToDate={handleEnterMoveMode}
      />
    )
  }, [
    popoverGame,
    venues,
    divisions,
    timeSlots,
    handlePopoverClose,
    handlePopoverSave,
    handleEnterMoveMode,
  ])

  // ─── Drag-and-drop handler ────────────────────────────────────
  const handleCalendarDragEnd = useCallback(
    (gameId: string, targetDate: Date) => {
      const game = filteredGames.find((g) => g.id === gameId)
      if (!game) {
        return
      }
      if (game.approvalStatus === 'approved') {
        return
      }

      const tY = targetDate.getFullYear()
      const tM = String(targetDate.getMonth() + 1).padStart(2, '0')
      const tD = String(targetDate.getDate()).padStart(2, '0')
      const targetDateStr = `${tY}-${tM}-${tD}`

      if (calendarBlackoutDateStrings.has(targetDateStr)) {
        message.warning('This date is blacked out')
        return
      }
      if (minDate && targetDateStr < minDate) {
        message.warning('This date is before the competition start date')
        return
      }
      if (maxDate && targetDateStr > maxDate) {
        message.warning('This date is after the competition end date')
        return
      }

      if (!requestId) {
        return
      }

      // Route through preflight modal
      requestPreview(requestId, gameId, { target_date: targetDateStr })
    },
    [
      filteredGames,
      requestPreview,
      calendarBlackoutDateStrings,
      message,
      minDate,
      maxDate,
      requestId,
    ]
  )

  // ─── Export handler ───────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (displayGames.length === 0) {
      return
    }
    const csv = generateGamesCsv({ games: displayGames, venues })
    const name = (competition?.name || 'Schedule')
      .replace(/[^a-zA-Z0-9_\- ]/g, '')
      .replace(/\s+/g, '_')
    downloadCsv(csv, `${name}_Games_${dayjs().format('YYYYMMDD')}.csv`)
  }, [displayGames, venues, competition])

  // ─── Selection state for bulk actions ─────────────────────────
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set())

  const selectedPendingIds = useMemo(
    () =>
      Array.from(selectedGameIds).filter((id) => {
        const game = filteredGames.find((g) => g.id === id)
        return (
          game && (game.approvalStatus === 'pending' || !game.approvalStatus)
        )
      }),
    [selectedGameIds, filteredGames]
  )

  const selectedApprovedIds = useMemo(
    () =>
      Array.from(selectedGameIds).filter((id) => {
        const game = filteredGames.find((g) => g.id === id)
        return game && game.approvalStatus === 'approved'
      }),
    [selectedGameIds, filteredGames]
  )

  const handleBulkApproveSelected = useCallback(async () => {
    if (selectedPendingIds.length === 0) {
      return
    }
    await handleBulkApprove(selectedPendingIds)
    setSelectedGameIds(new Set())
  }, [selectedPendingIds, handleBulkApprove])

  const handleBulkRevertSelected = useCallback(async () => {
    if (selectedApprovedIds.length === 0) {
      return
    }
    await handleBulkRevert(selectedApprovedIds)
    setSelectedGameIds(new Set())
  }, [selectedApprovedIds, handleBulkRevert])

  // ─── Navigation ───────────────────────────────────────────────
  const handleBackToSetup = useCallback(() => {
    history.push(`/scheduler-wizard/${competitionId}/configure/${requestId}`)
  }, [history, competitionId, requestId])

  // ─── Error toast ──────────────────────────────────────────────
  React.useEffect(() => {
    if (gameEditError) {
      message.error(gameEditError)
      clearGameEditError()
    }
  }, [gameEditError, message, clearGameEditError])

  // ─── Loading state ────────────────────────────────────────────
  const isLoading = !schedulerRequest
  const hasGames = displayGames.length > 0

  return (
    <App>
      <SchedulerErrorBoundary>
        <div css={pageContainerStyles}>
          {/* Header */}
          <header css={headerStyles}>
            <div css={headerInnerStyles}>
              <div css={leftSectionStyles}>
                <button
                  css={backLinkStyles}
                  onClick={handleBackToSetup}
                  type='button'
                >
                  <Icon
                    name='chevron-left'
                    fontSize={16}
                    color={colors.PRIMARY_300}
                  />
                  BACK TO SCHEDULE SETUP
                </button>
                <h1 css={titleStyles}>Calendar View</h1>
              </div>

              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <button
                  css={exportButtonStyles}
                  onClick={handleSaveCheckpoint}
                  type='button'
                  disabled={!hasGames || isSaving || isRestoring}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                {schedulerRequest?.has_changes_since_checkpoint && (
                  <button
                    css={css`
                      ${exportButtonStyles};
                      border-color: ${colors.WARNING_300};
                      color: ${colors.WARNING_300};
                      &:hover {
                        background: ${colors.WARNING_300}15;
                      }
                    `}
                    onClick={handleRestoreCheckpoint}
                    type='button'
                    disabled={isSaving || isRestoring}
                  >
                    {isRestoring ? 'Undoing...' : 'Undo'}
                  </button>
                )}
                <button
                  css={exportButtonStyles}
                  onClick={handleExport}
                  type='button'
                  disabled={!hasGames}
                >
                  <Icon name='download' fontSize={20} color={colors.WHITE} />
                  Export
                </button>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <div css={contentContainerStyles}>
            {isLoading ? (
              <div css={loadingStyles}>Loading schedule data...</div>
            ) : (
              <>
                {/* Filters */}
                <ScheduleFilters
                  divisions={filterOptions.divisions}
                  teams={filterOptions.teams}
                  locations={filterOptions.locations}
                  surfaces={filterOptions.surfaces}
                  statuses={filterOptions.statuses}
                  filters={gameFilters}
                  onFilterChange={setGameFilters}
                />

                {/* Toolbar: view toggle + bulk actions */}
                <div css={toolbarStyles}>
                  <div css={viewToggleContainerStyles}>
                    <button
                      type='button'
                      css={viewToggleBtnStyles(viewMode === 'calendar')}
                      onClick={() => setViewMode('calendar')}
                    >
                      Monthly
                    </button>
                    <button
                      type='button'
                      css={viewToggleBtnStyles(viewMode === 'week')}
                      onClick={() => setViewMode('week')}
                    >
                      Week
                    </button>
                    <button
                      type='button'
                      css={viewToggleBtnStyles(viewMode === 'day')}
                      onClick={() => setViewMode('day')}
                    >
                      Day
                    </button>
                    <button
                      type='button'
                      css={viewToggleBtnStyles(viewMode === 'table')}
                      onClick={() => setViewMode('table')}
                    >
                      Agenda
                    </button>
                  </div>

                  <div
                    css={css`
                      display: flex;
                      gap: 8px;
                      align-items: center;
                    `}
                  >
                    {selectedApprovedIds.length > 0 && (
                      <button
                        type='button'
                        onClick={handleBulkRevertSelected}
                        disabled={gameApproving}
                        css={css`
                          ${actionBtnStyles};
                          background: ${colors.WARNING_300};
                          border-color: ${colors.WARNING_300};
                          &:hover {
                            background: ${colors.WARNING_400};
                            border-color: ${colors.WARNING_400};
                          }
                          &:disabled {
                            opacity: 0.6;
                            cursor: not-allowed;
                          }
                        `}
                      >
                        <p
                          css={css`
                            ${actionBtnTextStyles};
                            color: ${colors.WHITE};
                          `}
                        >
                          {gameApproving
                            ? 'Reverting...'
                            : `Revert Selected (${selectedApprovedIds.length})`}
                        </p>
                      </button>
                    )}
                    {selectedPendingIds.length > 0 && (
                      <button
                        type='button'
                        onClick={handleBulkApproveSelected}
                        disabled={gameApproving}
                        css={css`
                          ${actionBtnStyles};
                          background: ${colors.SUCCESS_300};
                          border-color: ${colors.SUCCESS_300};
                          &:hover {
                            background: ${colors.SUCCESS_400};
                            border-color: ${colors.SUCCESS_400};
                          }
                          &:disabled {
                            opacity: 0.6;
                            cursor: not-allowed;
                          }
                        `}
                      >
                        <p
                          css={css`
                            ${actionBtnTextStyles};
                            color: ${colors.WHITE};
                          `}
                        >
                          {gameApproving
                            ? 'Approving...'
                            : `Approve Selected (${selectedPendingIds.length})`}
                        </p>
                      </button>
                    )}
                  </div>
                </div>

                {/* Calendar / Week / Table views */}
                {viewMode === 'calendar' && (
                  <div css={gridContainerStyles}>
                    <div css={calendarNavStyles}>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handlePrevMonth}
                        aria-label='Previous month'
                      >
                        &#8249;
                      </button>
                      <span css={calendarMonthTitleStyles}>
                        {MONTH_NAMES[calendarMonth]} {calendarYear}
                      </span>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handleNextMonth}
                        aria-label='Next month'
                      >
                        &#8250;
                      </button>
                      <button
                        type='button'
                        css={css`
                          ${calendarNavBtnStyles};
                          font-size: 13px;
                          padding: 4px 12px;
                          margin-left: 8px;
                        `}
                        onClick={() => setCurrentDate(new Date())}
                        aria-label='Go to today'
                      >
                        Today
                      </button>
                    </div>
                    <div
                      css={css`
                        padding: 0 16px 16px;
                        background: ${colors.SECONDARY_400};
                      `}
                    >
                      <CalendarGrid
                        year={calendarYear}
                        month={calendarMonth}
                        games={calendarGames}
                        blackoutDates={calendarBlackoutDates}
                        onGameClick={handleCalendarGameClick}
                        popoverGameId={popoverGameId}
                        popoverContent={popoverContent}
                        onPopoverOpenChange={setPopoverGameId}
                        onDragEnd={handleCalendarDragEnd}
                        onDayClick={moveMode ? handleMoveModeClick : undefined}
                        timeSlots={timeSlots}
                        competitionStartDate={minDate}
                        competitionEndDate={maxDate}
                        constraints={constraints}
                      />
                    </div>
                  </div>
                )}

                {viewMode === 'week' && (
                  <div css={gridContainerStyles}>
                    <div css={calendarNavStyles}>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handlePrevWeek}
                        aria-label='Previous week'
                      >
                        &#8249;
                      </button>
                      <span css={calendarMonthTitleStyles}>
                        {weekRangeLabel}
                      </span>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handleNextWeek}
                        aria-label='Next week'
                      >
                        &#8250;
                      </button>
                      <button
                        type='button'
                        css={css`
                          ${calendarNavBtnStyles};
                          font-size: 13px;
                          padding: 4px 12px;
                          margin-left: 8px;
                        `}
                        onClick={() => setCurrentDate(new Date())}
                        aria-label='Go to today'
                      >
                        Today
                      </button>
                    </div>
                    <div
                      css={css`
                        padding: 0 16px 16px;
                        background: ${colors.SECONDARY_400};
                      `}
                    >
                      <WeekGrid
                        weekStart={weekStart}
                        games={calendarGames}
                        blackoutDates={calendarBlackoutDates}
                        onGameClick={handleCalendarGameClick}
                        popoverGameId={popoverGameId}
                        popoverContent={popoverContent}
                        onPopoverOpenChange={setPopoverGameId}
                        onDragEnd={handleCalendarDragEnd}
                        timeSlots={timeSlots}
                        competitionStartDate={minDate}
                        competitionEndDate={maxDate}
                        constraints={constraints}
                      />
                    </div>
                  </div>
                )}

                {viewMode === 'day' && (
                  <div css={gridContainerStyles}>
                    <div css={calendarNavStyles}>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handlePrevDay}
                        aria-label='Previous day'
                      >
                        &#8249;
                      </button>
                      <span css={calendarMonthTitleStyles}>{dayLabel}</span>
                      <button
                        type='button'
                        css={calendarNavBtnStyles}
                        onClick={handleNextDay}
                        aria-label='Next day'
                      >
                        &#8250;
                      </button>
                      <button
                        type='button'
                        css={css`
                          ${calendarNavBtnStyles};
                          font-size: 13px;
                          padding: 4px 12px;
                          margin-left: 8px;
                        `}
                        onClick={() => setCurrentDate(new Date())}
                        aria-label='Go to today'
                      >
                        Today
                      </button>
                    </div>
                    <div
                      css={css`
                        padding: 0 16px 16px;
                        background: ${colors.SECONDARY_400};
                        overflow-x: auto;
                      `}
                    >
                      <DayGrid
                        selectedDate={calendarDate}
                        games={calendarGames}
                        venues={venues}
                        onGameClick={handleCalendarGameClick}
                        popoverGameId={popoverGameId}
                        popoverContent={popoverContent}
                        onPopoverOpenChange={setPopoverGameId}
                      />
                    </div>
                  </div>
                )}

                {viewMode === 'table' && (
                  <AgendaView
                    games={filteredGames}
                    venues={venues}
                    blackoutDates={calendarBlackoutDateStrings}
                    onGameClick={(game) => {
                      popoverSnapshotRef.current = {
                        locationId: game.locationId ?? '',
                        surfaceId: game.surfaceId ?? '',
                        homeTeamId: game.homeTeamId ?? '',
                        awayTeamId: game.awayTeamId ?? '',
                        startsAt: game.startsAt ?? '',
                        endsAt: game.endsAt ?? '',
                      }
                    }}
                    popoverGameId={popoverGameId}
                    popoverContent={popoverContent}
                    onPopoverOpenChange={setPopoverGameId}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </SchedulerErrorBoundary>

      {/* Move mode banner */}
      {moveMode && (
        <div css={moveModeBannerStyles}>
          <span>
            Click a target date to move Game #{moveMode.game.gameNumber}
          </span>
          <button
            type='button'
            onClick={handleExitMoveMode}
            css={moveModeCloseStyles}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Undo/Redo floating buttons */}
      <div css={undoRedoContainerStyles}>
        <button
          type='button'
          css={undoRedoButtonStyles(!canUndo)}
          disabled={!canUndo}
          title={
            lastEntry ? `Undo: ${lastEntry.description}` : 'Nothing to undo'
          }
          onClick={() => requestId && undo(requestId)}
        >
          ↩
        </button>
        <button
          type='button'
          css={undoRedoButtonStyles(!canRedo)}
          disabled={!canRedo}
          title={
            nextRedoEntry
              ? `Redo: ${nextRedoEntry.description}`
              : 'Nothing to redo'
          }
          onClick={() => requestId && redo(requestId)}
        >
          ↪
        </button>
      </div>

      {/* Preflight Validation Modal */}
      <PreflightValidationModal
        visible={mutationModalVisible}
        preview={mutationPreview}
        loading={mutationLoading}
        confirming={mutationConfirming}
        error={mutationError}
        onConfirm={handleConfirmMutation}
        onCancel={cancelMutation}
        onSelectAlternative={selectAlternativeSlot}
      />
    </App>
  )
}

// ─── Move mode & Undo/Redo styles ────────────────────────────────

const moveModeBannerStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 20px;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
`

const moveModeCloseStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.WHITE};
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

const undoRedoContainerStyles = css`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999;
  display: flex;
  gap: 4px;
`

const undoRedoButtonStyles = (disabled: boolean) => css`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: ${disabled ? colors.SECONDARY_300 : colors.HEADER};
  color: ${disabled ? colors.NEUTRAL_50 : colors.WHITE};
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 8px;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.5 : 1};
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${colors.SECONDARY_400};
  }
`

export default CalendarPage
